<script setup>
import { ref, onBeforeMount } from "vue";
// import useValidation from "@/utils/helpers/validations";
import ColorThief from "colorthief";
import FileUpload from "primevue/fileupload";

const emit = defineEmits(["refresh", "generateFromLogo", "update:visible"]);

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
});

// const global_user_name = inject(['global_user_name']);
// const { checkApkUrlValidation } = useValidation();

let isSubmitted = ref(false);
let logoFile = ref(null);
let url = ref(null);
let type = ref("logo");
let isValidUrl = ref(true);
let resultColors = ref([]);
let previewUrl = ref(null);
let logoImage = ref(null);
const logoFileInput = ref(null);
const errorMessage = ref("");

// const getModalInfo = computed(() => store.getters.getModalInfo);
// const getModalAction = computed(() => store.getters.getModalAction);

const validUrl = () => {
  isValidUrl.value = true;
  resultColors.value = [];
  previewUrl.value = null;
  isLoading.value = false;

  errorMessage.value = "";
  if (url.value && url.value.length > 0) {
    // Basic validation or use checkApkUrlValidation if it supports image URLs
    // For now, assuming checkApkUrlValidation checks for generic URL validity
    // If checkApkUrlValidation is too strict (e.g. requires .apk), we might need a looser check
    // isValidUrl.value = checkApkUrlValidation(url.value);

    // Looser check since image URLs might not end with extensions or might be query params
    isValidUrl.value = /^(http|https):\/\/[^ "]+$/.test(url.value);

    if (isValidUrl.value) {
      previewUrl.value = url.value;
      isLoading.value = true;
    } else {
      errorMessage.value = "Invalid URL format.";
    }
  }
  return isValidUrl.value;
};

const onFileChange = (event) => {
  isLoading.value = false;
  errorMessage.value = "";
  // PrimeVue FileUpload @select fires with { originalEvent, files: File[] }
  // Native input change fires with event.target.files
  const file = event?.files?.[0] ?? event?.target?.files?.[0];
  if (file) {
    logoFile.value = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      previewUrl.value = e.target.result;
      isLoading.value = true; // Start loading when preview is set, will finish in extractColors
    };
    reader.readAsDataURL(file);
  }
};

const calculateColorPercentages = (img, palette) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;

  // Resize for performance if needed, but keeping it simple for now or use a smaller max dimension
  const maxDim = 100;
  let scale = 1;
  if (width > maxDim || height > maxDim) {
    scale = Math.min(maxDim / width, maxDim / height);
  }
  canvas.width = width * scale;
  canvas.height = height * scale;

  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  const colorCounts = new Array(palette.length).fill(0);

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    // Skip transparent pixels
    if (a < 128) continue;

    let minDistance = Infinity;
    let closestColorIndex = -1;

    for (let j = 0; j < palette.length; j++) {
      const [pr, pg, pb] = palette[j];
      const distance = Math.sqrt(
        Math.pow(r - pr, 2) + Math.pow(g - pg, 2) + Math.pow(b - pb, 2),
      );
      if (distance < minDistance) {
        minDistance = distance;
        closestColorIndex = j;
      }
    }

    if (closestColorIndex !== -1) {
      colorCounts[closestColorIndex]++;
    }
  }

  const effectiveTotalPixels = colorCounts.reduce((a, b) => a + b, 0);

  return palette
    .map((color, index) => ({
      r: color[0],
      g: color[1],
      b: color[2],
      percentage: effectiveTotalPixels
        ? Math.round((colorCounts[index] / effectiveTotalPixels) * 100)
        : 0,
      selected: false, // Initialize selection state
      copied: false, // Initialize copied state
    }))
    .sort((a, b) => b.percentage - a.percentage);
};

const isLoading = ref(false);

const extractColors = () => {
  isLoading.value = true;
  resultColors.value = []; // Clear previous results while loading

  try {
    const colorThief = new ColorThief();
    const img = logoImage.value;

    const processImage = () => {
      try {
        const palette = colorThief.getPalette(img, 5);
        resultColors.value = calculateColorPercentages(img, palette);
      } catch (err) {
        console.error("Error processing image:", err);
      } finally {
        isLoading.value = false;
      }
    };

    if (img.complete) {
      processImage();
    } else {
      img.addEventListener("load", processImage, { once: true });
      img.addEventListener("error", () => {
        console.error("Error loading image for ColorThief");
        isLoading.value = false;
      }, { once: true });
    }
  } catch (error) {
    console.error("Error extracting colors:", error);
    isLoading.value = false;
  }
};

const toggleColorSelection = (index) => {
  resultColors.value.forEach((color, i) => {
    color.selected = i === index;
  });
};

const copyToClipboard = (colorObj) => {
  const text = rgbToHex(colorObj.r, colorObj.g, colorObj.b);

  if (navigator.clipboard) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        showCopiedFeedback(colorObj);
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  } else {
    // Fallback
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand("copy");
      showCopiedFeedback(colorObj);
    } catch (err) {
      console.error("Fallback: Oops, unable to copy", err);
    }
    document.body.removeChild(textArea);
  }
};

const showCopiedFeedback = (colorObj) => {
  colorObj.copied = true;
  setTimeout(() => {
    colorObj.copied = false;
  }, 2000);
};

const onApply = () => {
  isSubmitted.value = true;
  // Create new array with hex values for all colors
  const allColorsWithHex = resultColors.value.map((color) => ({
    ...color,
    hex: rgbToHex(color.r, color.g, color.b),
  }));

  emit("generateFromLogo", {
    colors: allColorsWithHex,
    source: type.value === "logo" ? logoFile.value : url.value,
    type: type.value,
  });
  changeLogoSourceVal();
};

const handleImageError = (e) => {
  console.error("Image failed to load", e);
  isLoading.value = false;
  isValidUrl.value = false;
  // errorMessage.value =
  //   "Unable to access image. This is likely due to CORS policy (security) on the external website blocking access. Please try uploading the image file instead.";
  errorMessage.value =
    "Oops! We're having trouble loading that. The website hosting this image has restricted outside access. Not to worry—you can easily get around this by uploading the image file directly to your dashboard";
  // Maybe clear previewUrl so broken image goes away entirely?
  // User wants to "check if image loaded or not to avoid this".
  // If we set previewUrl = null, the whole preview div disappears (v-if="previewUrl").
  previewUrl.value = null;
  resultColors.value = [];
};

// ... (other refs)

// ... (other refs)

const changeLogoSourceVal = () => {
  logoFile.value = null;
  url.value = null;
  // PrimeVue FileUpload exposes .clear(); fall back to native input reset.
  if (logoFileInput.value?.clear) {
    logoFileInput.value.clear();
  } else if (logoFileInput.value) {
    logoFileInput.value.value = "";
  }
  isValidUrl.value = true;
  resultColors.value = [];
  previewUrl.value = null;
  isSubmitted.value = false;
  logoImage.value = null;
  isLoading.value = false;
  errorMessage.value = "";
};

const rgbToHex = (r, g, b) => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};
// ... (add onMounted import at top if missing - checking first)
// It was not imported. Need to update imports first.
// });

onBeforeMount(() => {
  changeLogoSourceVal();
});
</script>

<template>
  <DModal
    :visible="props.visible"
    @update:visible="emit('update:visible', $event)"
    :main_classes="[]"
    :dialog_classes="[]"
  >
    <template #title> Generate From Logo </template>

    <template #body>
      <div class="delete-text mb-3 text-left">
        <span>Upload the logo you want to <b>Generate Colors From it</b>.</span>
      </div>

      <div class="w-full flex unique-radio-inputs p-0 mb-3">
        <div class="w-1/2 radioOne p-0">
          <input
            @change="changeLogoSourceVal"
            class="form-check-input"
            type="radio"
            name="type"
            id="logoOption"
            value="logo"
            v-model="type"
          />
          <label class="form-check-label" for="logoOption">Logo</label>
        </div>

        <div class="w-1/2 radioTwo p-0">
          <input
            @change="changeLogoSourceVal"
            class="form-check-input"
            type="radio"
            name="type"
            id="partnerOption"
            value="url"
            v-model="type"
          />
          <label class="form-check-label" for="partnerOption">URL</label>
        </div>
      </div>

      <div v-if="type == 'logo'" class="logo-upload-field mb-3">
        <label class="logo-upload-field__label">Logo</label>
        <FileUpload
          ref="logoFileInput"
          mode="basic"
          name="logo"
          accept="image/*"
          custom-upload
          :auto="true"
          choose-label="Choose File"
          choose-icon="pi pi-upload"
          class="logo-upload-field__input"
          @select="onFileChange"
        />
        <span v-if="!logoFile" class="logo-upload-field__placeholder">
          No file chosen
        </span>
        <span v-else class="logo-upload-field__filename">
          {{ logoFile.name }}
        </span>
      </div>

      <div v-else class="mb-3">
        <FloatLabel class="url-field">
          <PInput
            id="url"
            v-model="url"
            :class="{ 'p-invalid': !isValidUrl || errorMessage }"
            @input="validUrl"
          />
          <label for="url">URL</label>
        </FloatLabel>
        <div v-if="!isValidUrl || errorMessage" class="invalid-feedback mt-1">
          {{ errorMessage || "Invalid URL." }}
        </div>
      </div>

      <!-- Preview Image for ColorThief -->
      <div v-if="previewUrl" class="flex justify-center mb-3">
        <img
          v-show="!isLoading"
          ref="logoImage"
          :src="previewUrl"
          alt="Logo Preview"
          crossorigin="anonymous"
          @load="extractColors"
          @error="handleImageError"
          style="max-width: 416px; height: auto"
        />
      </div>

      <div v-if="isLoading" class="flex justify-center my-3">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-if="resultColors.length" class="mt-3">
        <label class="form-label font-bold mb-2"
          >Select color as primary brand color:</label
        >
        <div class="result-colors flex gap-3 flex-wrap">
          <div
            v-for="(color, index) in resultColors"
            :key="index"
            class="flex flex-col items-center"
          >
            <div
              class="color-box"
              @click="toggleColorSelection(index)"
              :style="{
                backgroundColor: `rgb(${color.r}, ${color.g}, ${color.b})`,
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                border: color.selected ? '3px solid #000' : '1px solid #ddd',
                boxShadow: color.selected ? '0 0 0 2px #fff inset' : 'none',
                cursor: 'pointer',
                marginBottom: '5px',
                transform: color.selected ? 'scale(1.1)' : 'scale(1)',
                transition: 'all 0.2s ease',
              }"
              :title="rgbToHex(color.r, color.g, color.b)"
            ></div>
            <!-- <span class="small font-bold">{{ color.percentage }}%</span> -->
            <span
              v-if="!color.copied"
              class="small text-gray-500"
              style="font-size: 10px; cursor: pointer"
              title="Click to copy"
              @click.stop="copyToClipboard(color)"
              >{{ rgbToHex(color.r, color.g, color.b) }}</span
            >
            <span v-else class="small text-success" style="font-size: 10px">
              <i class="bi bi-check-lg"></i> Copied
            </span>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="w-full">
        <div class="seperator text-center"></div>
        <div class="flex mt-20 justify-center">
          <button
            :disabled="(!logoFile && !url) || isSubmitted || !isValidUrl"
            @click="onApply"
            type="button"
            class="btn btn-primary add-button mr-30"
          >
            Apply
            <span
              v-if="isSubmitted"
              class="spinner-sm"
              role="status"
              aria-hidden="true"
            ></span>
          </button>
          <button
            @click="changeLogoSourceVal(); emit('update:visible', false)"
            type="button"
            class="btn btn-secondary cancel-button"
          >
            Cancel
          </button>
        </div>
      </div>
    </template>
  </DModal>
</template>

<style scoped>
/* PrimeVue FileUpload (basic mode) — match the general DInput style */
.logo-upload-field {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 48px;
  padding: 14px 14px 10px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.logo-upload-field:focus-within {
  border-color: var(--primary-color, #4e7fff);
  box-shadow: 0 0 0 3px rgba(78, 127, 255, 0.12);
}

.logo-upload-field__label {
  position: absolute;
  top: 6px;
  left: 14px;
  margin: 0;
  padding: 0 4px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  color: var(--primary-color, #4e7fff);
  background: #ffffff;
  pointer-events: none;
  z-index: 1;
}

.logo-upload-field__input {
  flex-shrink: 0;
}

.logo-upload-field__input :deep(.p-fileupload-basic),
.logo-upload-field__input :deep(.p-fileupload) {
  display: inline-flex;
  align-items: center;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  box-shadow: none;
}

.logo-upload-field__input :deep(.p-button.p-fileupload-choose),
.logo-upload-field__input :deep(.p-fileupload-choose.p-button) {
  display: inline-flex !important;
  align-items: center !important;
  height: 32px !important;
  padding: 0 12px !important;
  border-radius: 4px !important;
  background: #f9fafb !important;
  border: 1px solid #d1d5db !important;
  color: #1f2937 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  line-height: 1 !important;
  box-shadow: none !important;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.logo-upload-field__input :deep(.p-button.p-fileupload-choose:hover),
.logo-upload-field__input :deep(.p-fileupload-choose.p-button:hover) {
  background: #f3f4f6 !important;
  border-color: #9ca3af !important;
  color: #1f2937 !important;
}

.logo-upload-field__input :deep(.p-button.p-fileupload-choose:focus),
.logo-upload-field__input :deep(.p-fileupload-choose.p-button:focus) {
  box-shadow: 0 0 0 3px rgba(78, 127, 255, 0.18) !important;
}

.logo-upload-field__input :deep(.p-button.p-fileupload-choose .p-button-icon),
.logo-upload-field__input :deep(.p-fileupload-choose.p-button .p-button-icon) {
  margin-right: 6px !important;
  font-size: 12px !important;
  color: inherit !important;
}

.logo-upload-field__input :deep(.p-button.p-fileupload-choose .p-button-label),
.logo-upload-field__input :deep(.p-fileupload-choose.p-button .p-button-label) {
  font-weight: 500 !important;
  color: inherit !important;
}

/* Hide PrimeVue's built-in filename render (we render our own) */
.logo-upload-field__input :deep(.p-fileupload-filename) {
  display: none;
}

.logo-upload-field__placeholder,
.logo-upload-field__filename {
  margin-left: 12px;
  font-size: 14px;
  line-height: 1.2;
  color: #1f2937;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.logo-upload-field__placeholder {
  color: #94a3b8;
}

/* URL field — full-width override (global PInput style is fixed-width 416px) */
.url-field {
  display: block;
  width: 100%;
}

.url-field :deep(.p-inputtext) {
  width: 100% !important;
  max-width: 100% !important;
}

/* Radio row */
.unique-radio-inputs .form-check-label {
  margin-left: 8px;
  color: #1f2937;
  cursor: pointer;
}

/* Result colors section label */
.result-colors {
  margin-top: 4px;
}

/* ── Dark mode ─────────────────────────────────────────────── */
[data-theme="dark"] .logo-upload-field {
  background: #28364e;
  border-color: #3d5070;
}

[data-theme="dark"] .logo-upload-field:focus-within {
  border-color: #4e7fff;
  box-shadow: 0 0 0 3px rgba(78, 127, 255, 0.15);
}

[data-theme="dark"] .logo-upload-field__label {
  background: #28364e;
  color: #6b96ff;
}

[data-theme="dark"] .logo-upload-field__input :deep(.p-button.p-fileupload-choose),
[data-theme="dark"] .logo-upload-field__input :deep(.p-fileupload-choose.p-button) {
  background: #324663 !important;
  border-color: #4e6488 !important;
  color: #e8edf5 !important;
}

[data-theme="dark"] .logo-upload-field__input :deep(.p-button.p-fileupload-choose:hover),
[data-theme="dark"] .logo-upload-field__input :deep(.p-fileupload-choose.p-button:hover) {
  background: #3d5070 !important;
  border-color: #5b7393 !important;
  color: #e8edf5 !important;
}

[data-theme="dark"] .logo-upload-field__placeholder {
  color: #6480a0;
}

[data-theme="dark"] .logo-upload-field__filename {
  color: #e8edf5;
}


[data-theme="dark"] .unique-radio-inputs .form-check-label {
  color: #e8edf5;
}

[data-theme="dark"] .delete-text {
  color: #9aadc4;
}

[data-theme="dark"] .delete-text b {
  color: #e8edf5;
}

[data-theme="dark"] .seperator {
  background-color: #2d3f5a;
  height: 1px;
  opacity: 1;
}

[data-theme="dark"] .invalid-feedback {
  color: #f87171;
}

[data-theme="dark"] .form-label {
  color: #e8edf5;
}

[data-theme="dark"] :deep(.text-gray-500) {
  color: #9aadc4 !important;
}
</style>
